import { CalendarClock, CircleAlert, CircleCheck, Clock, Wallet } from "lucide-react";
import { addDays, format, parseISO } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import type { StayPlanStatus } from "@/hooks/useStayPlanStatus";
import { formatDateBR } from "@/lib/date-utils";

export interface PlanBillingPanelProps {
  status: StayPlanStatus;
  className?: string;
  onChargeNow: () => void;
  onChangeBilling: () => void;
  charging: boolean;
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/** O bloco de cobrança muda de cor com o estado do dinheiro, como no design. */
const TONE = {
  paid: {
    box: "border-state-success/30 bg-state-success-soft",
    text: "text-state-success-foreground",
    pill: "bg-background/70",
    Icon: CircleCheck,
    label: "planCard.settled",
  },
  overdue: {
    box: "border-state-danger/30 bg-state-danger-soft",
    text: "text-state-danger-foreground",
    pill: "bg-background/70",
    Icon: CircleAlert,
    label: "planCard.overdue",
  },
  open: {
    box: "border-amber-200 bg-state-warning-soft",
    text: "text-state-warning-foreground",
    pill: "bg-background/65",
    Icon: Clock,
    label: "planCard.toPay",
  },
} as const;

export function PlanBillingPanel({ status, onChargeNow, onChangeBilling, charging, className = "" }: PlanBillingPanelProps) {
  const { t, i18n } = useTranslation("patients");
  const locale = i18n.language.startsWith("pt") ? ptBR : enUS;
  const { contract } = status;

  // Pacote de diárias não tem ciclo nem mensalidade: cobra-se o pacote uma vez.
  const isPackage = contract.kind === "day_package";
  const isPaid = status.paymentStatus === "paid";
  const tone = TONE[isPaid ? "paid" : status.paymentStatus === "overdue" ? "overdue" : "open"];

  const title = isPackage
    ? t("planCard.packageTitle")
    : status.cycle
      ? t("planCard.billingTitleMonth", { month: format(parseISO(status.cycle.start), "MMMM", { locale }) })
      : t("planCard.billingTitle");
  const renewalOn = status.cycle
    ? formatDateBR(format(addDays(parseISO(status.cycle.end), 1), "yyyy-MM-dd"))
    : null;

  return (
    <div className={`flex flex-col ${className}`}>
      <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-color-light">{title}</p>

      <div className={`mt-3 rounded-lg border p-4 ${tone.box}`}>
        <div className="flex flex-wrap items-center gap-2">
          <tone.Icon className={`h-[18px] w-[18px] shrink-0 ${tone.text}`} aria-hidden />
          <span className={`font-display text-[0.9375rem] font-bold ${tone.text}`}>{t(tone.label)}</span>
          <span className={`ms-auto font-display text-xl font-extrabold ${tone.text}`}>
            {formatCurrency(status.openAmount ?? contract.price, i18n.language)}
          </span>
        </div>

        {/* A escolha de quando cobrar é de configuração; o design pede que ela
            fique visível no card, não escondida no dialog. Um pacote não tem
            ciclo, então a linha não se aplica a ele. */}
        {/* Pacote não tem ciclo nem renovação, mas tem prazo: no pós-pago a
            cobrança só vence 30 dias depois. É aqui que essa data mora — no
            cabeçalho ela seria lida como validade das diárias, que não existe. */}
        {isPackage ? (
          status.dueOn ? (
            <p className={`mt-3 flex w-fit items-center gap-2 rounded-full px-3 py-1.5 ${tone.pill}`}>
              <CalendarClock className={`h-3.5 w-3.5 shrink-0 ${tone.text}`} aria-hidden />
              <span className={`text-xs font-semibold ${tone.text}`}>
                {t("planCard.cycleOpenDue", { date: formatDateBR(status.dueOn) })}
              </span>
            </p>
          ) : null
        ) : (
          <>
            <p className={`mt-3 flex w-fit items-center gap-2 rounded-full px-3 py-1.5 ${tone.pill}`}>
              <CalendarClock className={`h-3.5 w-3.5 shrink-0 ${tone.text}`} aria-hidden />
              <span className={`text-xs font-semibold ${tone.text}`}>
                {contract.billingTiming === "end" ? t("planCard.billingAtEnd") : t("planCard.billingAtStart")}
                {status.dueOn ? ` · ${formatDateBR(status.dueOn)}` : ""}
              </span>
            </p>
            <p className={`mt-2.5 text-xs leading-relaxed ${tone.text} opacity-80`}>{t("planCard.billingHint")}</p>
            {status.cycle ? (
              <div className={`mt-2.5 space-y-1 text-xs ${tone.text} opacity-80`}>
                <p>{t("planCard.cyclePeriod", {
                  start: formatDateBR(status.cycle.start),
                  end: formatDateBR(status.cycle.end),
                })}</p>
                <p>{t("planCard.renewsOn", { date: renewalOn })}</p>
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {isPaid ? null : (
          <Button type="button" className="flex-1" disabled={charging} onClick={onChargeNow}>
            <Wallet className="me-2 h-4 w-4" aria-hidden />
            {t("planCard.chargeNow")}
          </Button>
        )}
        {isPackage ? null : (
          <Button type="button" variant="outline" onClick={onChangeBilling}>
            {t("planCard.changeBilling")}
          </Button>
        )}
      </div>
    </div>
  );
}
